import { router } from "@inertiajs/react"
import { toast } from "sonner"
import { IconTemperature, IconBell } from "@tabler/icons-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ApiTokenDisplay } from "../api-token-display"
import type { Device, DeviceSettings } from "@/types/device"

interface SettingsPanelProps {
  device: Device
}

export function SettingsPanel({ device }: SettingsPanelProps) {
  const currentSettings: DeviceSettings = device.settings ?? {
    temp_unit: "fahrenheit",
    notifications_enabled: false,
  }

  const updateSetting = (updates: Partial<DeviceSettings>) => {
    const newSettings = { ...currentSettings, ...updates }

    router.patch(
      `/devices/${device.id}`,
      { settings: JSON.stringify(newSettings) },
      {
        preserveScroll: true,
        only: ["device"],
        onSuccess: () => {
          toast.success("Settings saved")
        },
        onError: () => {
          toast.error("Failed to save settings")
        },
      }
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-medium">Device Settings</h3>
        <p className="text-sm text-muted-foreground">
          Configure display preferences and notifications for this device
        </p>
      </div>

      {/* Display Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <IconTemperature className="size-5 text-orange-500" />
            <CardTitle className="text-lg">Display Preferences</CardTitle>
          </div>
          <CardDescription>Customize how data is displayed</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Label htmlFor="temp-unit">Temperature Unit</Label>
            <Select
              value={currentSettings.temp_unit}
              onValueChange={(v) =>
                updateSetting({ temp_unit: v as "celsius" | "fahrenheit" })
              }
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                <SelectItem value="celsius">Celsius (°C)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <IconBell className="size-5 text-blue-500" />
            <CardTitle className="text-lg">Notifications</CardTitle>
          </div>
          <CardDescription>Configure alerts for this device</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Label htmlFor="notifications">Enable Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive alerts when readings reach thresholds
              </p>
            </div>
            <Switch
              id="notifications"
              checked={currentSettings.notifications_enabled}
              onCheckedChange={(checked) =>
                updateSetting({ notifications_enabled: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* API Token */}
      {device.api_token && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">API Token</CardTitle>
            <CardDescription>
              Use this token to report readings from remote devices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ApiTokenDisplay token={device.api_token} />
            <p className="mt-2 text-xs text-muted-foreground">
              Include this token in the Authorization header when posting
              readings:
              <code className="ml-1 px-1 py-0.5 bg-muted rounded">
                Authorization: Bearer {"{token}"}
              </code>
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
